import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Fade,
  Chip,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  Business,
  Inventory,
  LocalShipping,
  Assessment,
  SmartToy,
  TrendingUp,
  Security,
  Speed,
  EmojiObjects,
  ArrowForward,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { smartOnboardingApi } from '../../api/smartOnboarding';

// Import step components
import WelcomeStep from './steps/WelcomeStep';
import CompanyProfileStep from './steps/CompanyProfileStep';
import SupplyChainMappingStep from './steps/SupplyChainMappingStep';
import DataIntegrationStep from './steps/DataIntegrationStep';
import HITLIntroductionStep from './steps/HITLIntroductionStep';
import AgentConfigurationStep from './steps/AgentConfigurationStep';
import FinalSetupStep from './steps/FinalSetupStep';

interface OnboardingStep {
  id: string;
  label: string;
  description: string;
  icon: React.ReactElement;
  component: React.ComponentType<StepComponentProps>;
  optional?: boolean;
}

interface StepComponentProps {
  onNext: (data?: any) => void;
  onBack: () => void;
  data: any;
  updateData: (data: any) => void;
}

const steps: OnboardingStep[] = [
  {
    id: 'welcome',
    label: 'Welcome to ASCIN',
    description: 'Introduction to the platform',
    icon: <EmojiObjects />,
    component: WelcomeStep,
  },
  {
    id: 'company',
    label: 'Company Profile',
    description: 'Tell us about your business',
    icon: <Business />,
    component: CompanyProfileStep,
  },
  {
    id: 'supply-chain',
    label: 'Supply Chain Mapping',
    description: 'Map your supply chain network',
    icon: <LocalShipping />,
    component: SupplyChainMappingStep,
  },
  {
    id: 'data',
    label: 'Data Integration',
    description: 'Connect your data sources',
    icon: <Inventory />,
    component: DataIntegrationStep,
    optional: true,
  },
  {
    id: 'hitl',
    label: 'Human-in-the-Loop',
    description: 'Learn about AI collaboration',
    icon: <SmartToy />,
    component: HITLIntroductionStep,
  },
  {
    id: 'agents',
    label: 'Agent Configuration',
    description: 'Configure your AI agents',
    icon: <Assessment />,
    component: AgentConfigurationStep,
  },
  {
    id: 'complete',
    label: 'Ready to Launch',
    description: 'Final setup and review',
    icon: <TrendingUp />,
    component: FinalSetupStep,
  },
];

const EnhancedOnboardingFlow: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [onboardingData, setOnboardingData] = useState<any>({});
  const [progress, setProgress] = useState(0);

  // Load saved progress on mount
  useEffect(() => {
    const savedData = localStorage.getItem('onboarding_data');
    const savedStep = localStorage.getItem('onboarding_current_step');
    const savedCompleted = localStorage.getItem('onboarding_completed_steps');
    
    if (savedData) {
      try {
        setOnboardingData(JSON.parse(savedData));
      } catch (e) {
        console.error('Error loading saved onboarding data:', e);
      }
    }
    
    if (savedStep) {
      setActiveStep(parseInt(savedStep, 10));
    }
    
    if (savedCompleted) {
      try {
        const completedArray = JSON.parse(savedCompleted);
        setCompletedSteps(new Set(completedArray));
      } catch (e) {
        console.error('Error loading completed steps:', e);
      }
    }
  }, []);

  useEffect(() => {
    // Calculate progress
    const totalSteps = steps.length;
    const completed = completedSteps.size;
    setProgress((completed / totalSteps) * 100);
    
    // Save progress to localStorage
    localStorage.setItem('onboarding_progress', String((completed / totalSteps) * 100));
  }, [completedSteps]);

  // Save progress incrementally
  const saveProgress = (stepData: any, stepIndex: number) => {
    const updatedData = {
      ...onboardingData,
      [steps[stepIndex].id]: stepData,
    };
    setOnboardingData(updatedData);
    
    // Persist to localStorage
    localStorage.setItem('onboarding_data', JSON.stringify(updatedData));
    localStorage.setItem('onboarding_current_step', String(stepIndex + 1));
    localStorage.setItem('onboarding_completed_steps', JSON.stringify([...Array.from(completedSteps), stepIndex]));
  };

  const handleNext = (stepData?: any) => {
    if (stepData) {
      saveProgress(stepData, activeStep);
    }

    setCompletedSteps((prev) => new Set(prev).add(activeStep));

    if (activeStep === steps.length - 1) {
      // Complete onboarding
      handleComplete();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSkip = () => {
    if (steps[activeStep].optional) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleComplete = async () => {
    // Save onboarding data
    try {
      // Send to backend
      console.log('Onboarding completed:', onboardingData);
      await smartOnboardingApi.complete(onboardingData);
      
      // Mark onboarding as complete in localStorage
      localStorage.setItem('onboarding_completed', 'true');
      localStorage.setItem('onboarding_data', JSON.stringify(onboardingData));
      
      // Set SSO auth token for Executive Force AI integration
      if (!localStorage.getItem('access_token')) {
        localStorage.setItem('access_token', 'sso_token_from_executive_force_ai');
        localStorage.setItem('user', JSON.stringify({
          name: onboardingData.company?.companyName || 'Sentient User',
          email: onboardingData.company?.email || 'user@sentient.ai',
          role: 'user',
        }));
      }
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      // Still navigate to dashboard even if API fails
      // The user can use the app with demo data
      localStorage.setItem('onboarding_completed', 'true');
      if (!localStorage.getItem('access_token')) {
        localStorage.setItem('access_token', 'sso_token_from_executive_force_ai');
        localStorage.setItem('user', JSON.stringify({
          name: 'Sentient User',
          email: 'user@sentient.ai',
          role: 'user',
        }));
      }
      navigate('/dashboard');
    }
  };

  const updateData = (data: any) => {
    setOnboardingData((prev: any) => ({
      ...prev,
      [steps[activeStep].id]: {
        ...prev[steps[activeStep].id],
        ...data,
      },
    }));
  };

  const CurrentStepComponent = steps[activeStep].component;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            Welcome to ASCIN
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Let's set up your Adaptive Supply Chain Intelligence Network
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            background: 'white',
            borderRadius: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
              Overall Progress
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  },
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ ml: 2, fontWeight: 600 }}>
              {Math.round(progress)}%
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.id} completed={completedSteps.has(index)}>
                <StepLabel
                  StepIconComponent={() => (
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: completedSteps.has(index)
                          ? theme.palette.success.main
                          : index === activeStep
                          ? theme.palette.primary.main
                          : theme.palette.grey[300],
                        color: 'white',
                      }}
                    >
                      {completedSteps.has(index) ? (
                        <CheckCircle />
                      ) : (
                        step.icon
                      )}
                    </Avatar>
                  )}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                  {step.optional && (
                    <Chip
                      label="Optional"
                      size="small"
                      sx={{ ml: 1, height: 20 }}
                    />
                  )}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Step Content */}
        <Fade in={true} timeout={500}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              background: 'white',
              borderRadius: 3,
              minHeight: 500,
            }}
          >
            <CurrentStepComponent
              onNext={handleNext}
              onBack={handleBack}
              data={onboardingData[steps[activeStep].id] || {}}
              updateData={updateData}
            />
          </Paper>
        </Fade>

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<ArrowBack />}
            sx={{ visibility: activeStep === 0 ? 'hidden' : 'visible' }}
          >
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {steps[activeStep].optional && (
              <Button onClick={handleSkip} color="secondary">
                Skip for Now
              </Button>
            )}
            <Button
              variant="contained"
              onClick={() => handleNext()}
              endIcon={<ArrowForward />}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                px: 4,
              }}
            >
              {activeStep === steps.length - 1 ? 'Complete Setup' : 'Continue'}
            </Button>
          </Box>
        </Box>

        {/* Help Section */}
        <Alert
          severity="info"
          icon={<EmojiObjects />}
          sx={{ mt: 3, borderRadius: 2 }}
        >
          <Typography variant="body2">
            <strong>Need help?</strong> Each step includes guided assistance and
            examples. You can always come back and update your settings later.
          </Typography>
        </Alert>
      </Container>
    </Box>
  );
};

export default EnhancedOnboardingFlow;
